import React from "react";
import { Box, BoxProps, SxProps, Theme, useTheme } from "@mui/material";

type IconGradientProps = BoxProps & {
  children: React.ReactNode;
  gradientVariant?: "default" | "subtle" | "vibrant" | "soft" | "bold";
  sx?: SxProps<Theme>;
};

/**
 * Reusable wrapper for icons with gradient-inspired styling
 * Uses gradient color stops to create visually appealing icon colors
 * that match the gradient design system
 */
const IconGradient: React.FC<IconGradientProps> = ({
  children,
  gradientVariant = "default",
  sx,
  ...props
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Get colors based on variant - using distinct, vibrant colors for each variant
  const getIconColor = () => {
    switch (gradientVariant) {
      case "subtle":
        // Muted gray-blue
        return isDark ? "#94a3b8" : "#64748b";
      case "vibrant":
        // Bright purple-blue
        return isDark ? "#a78bfa" : "#7c3aed";
      case "soft":
        // Very light, almost white
        return isDark ? "#f8fafc" : "#dbeafe";
      case "bold":
        // Pure white for maximum contrast
        return isDark ? "#ffffff" : "#1e40af";
      case "default":
      default:
        // Default gradient end color - light purple-blue
        return isDark ? "#c7d2fe" : "#3b82f6";
    }
  };

  const getHoverColor = () => {
    switch (gradientVariant) {
      case "subtle":
        return isDark ? "#cbd5e1" : "#475569";
      case "vibrant":
        return isDark ? "#c4b5fd" : "#8b5cf6";
      case "soft":
        return isDark ? "#ffffff" : "#bfdbfe";
      case "bold":
        return isDark ? "#e0e7ff" : "#2563eb";
      case "default":
      default:
        return isDark ? "#e0e7ff" : "#60a5fa";
    }
  };

  const getShadowColor = (isHover = false) => {
    const opacity = isHover ? 0.6 : 0.3;
    const hoverOpacity = isHover ? 0.8 : 0.5;
    
    switch (gradientVariant) {
      case "subtle":
        return isDark 
          ? `rgba(148, 163, 184, ${isHover ? hoverOpacity : opacity})` 
          : `rgba(100, 116, 139, ${isHover ? hoverOpacity : opacity})`;
      case "vibrant":
        return isDark 
          ? `rgba(167, 139, 250, ${isHover ? hoverOpacity : opacity})` 
          : `rgba(124, 58, 237, ${isHover ? hoverOpacity : opacity})`;
      case "soft":
        return isDark 
          ? `rgba(248, 250, 252, ${isHover ? hoverOpacity : opacity})` 
          : `rgba(219, 234, 254, ${isHover ? hoverOpacity : opacity})`;
      case "bold":
        return isDark 
          ? `rgba(255, 255, 255, ${isHover ? 0.8 : 0.5})` 
          : `rgba(30, 64, 175, ${isHover ? hoverOpacity : opacity})`;
      case "default":
      default:
        return isDark 
          ? `rgba(199, 210, 254, ${isHover ? hoverOpacity : opacity})` 
          : `rgba(59, 130, 246, ${isHover ? hoverOpacity : opacity})`;
    }
  };

  const iconColor = getIconColor();
  const hoverColor = getHoverColor();
  const shadowColor = getShadowColor(false);
  const hoverShadowColor = getShadowColor(true);

  return (
    <Box
      {...props}
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          "& svg": {
            color: `${iconColor} !important`,
            fill: `${iconColor} !important`,
            transition: "all 0.3s ease",
            filter: `drop-shadow(0 1px 3px ${shadowColor})`,
          },
          "&:hover svg": {
            color: `${hoverColor} !important`,
            fill: `${hoverColor} !important`,
            transform: "scale(1.1)",
            filter: `drop-shadow(0 2px 6px ${hoverShadowColor})`,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Box>
  );
};

export default IconGradient;

